import { useEffect, useState } from "react";
import OtpInput from "../../ui/OtpInput";
import Button from "../../ui/Button";
import * as validate from "../../../lib/validators";
import type { JoinMeetingContext } from "./JoinMeetingProfile";
import {
  resendPublicMeetingJoinOtpApi,
  verifyPublicMeetingJoinOtpApi,
} from "../../../api/publicMeetingJoinApi";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthProvider";
import { saveToken } from "../../../lib/isLoggedin";
import { setAccessToken } from "../../../state/tokenStore";
import { usePublicMeetingStore } from "../../../store/public.meeting.store";
import { openAdvocatePrejoinTab } from "../../../utils/advocateLobbyJoin";

const RESEND_TIME = 60;

interface JoinMeetingOTPProps {
  onNext?: () => void;
  onBack?: () => void;
  meetingDetails: JoinMeetingContext;
}

const JoinMeetingOTP = ({
  onNext,
  onBack,
  meetingDetails,
}: JoinMeetingOTPProps) => {
  const { setLoading: setAuthLoading, setUser } = useAuth();
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [loading, setLoading] = useState(false);

  const [counter, setCounter] = useState(RESEND_TIME);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();
  const authentication = false;

  const setMeetingState = usePublicMeetingStore(
    (state) => state.setMeetingState,
  );

  // Countdown Timer
  useEffect(() => {
    if (canResend) return;

    const timer = setInterval(() => {
      setCounter((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [canResend]);

  const handleResendOTP = async () => {
    if (!canResend || loading) return;

    try {
      setLoading(true);

      // Call your resend OTP API
      const resp = await resendPublicMeetingJoinOtpApi({
        mobile: meetingDetails.mobile.trim(),
        caseNumber: meetingDetails.caseNumber.trim().toUpperCase(),
        meetingId: meetingDetails.meetingId.trim(),
      });
      if (!resp?.data?.status) {
        toast.error(resp?.data?.message || "Something went wrong");
      } else {
        toast.success(resp?.data?.message || "Success");

        setCounter(RESEND_TIME);
        setCanResend(false);
        setOtp("");
      }
    } catch (error: any) {
      toast.error(error?.message || error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const error = validate.otp(otp);
    setOtpError(error);
    if (error) return;
    try {
      setMeetingState("prejoin");
      const res = await verifyPublicMeetingJoinOtpApi({
        meetingId: meetingDetails.meetingId.trim(),
        mobile: meetingDetails.mobile.trim(),
        caseNumber: meetingDetails.caseNumber.trim().toUpperCase(),
        otp,
      });
      if (!res?.data?.status) {
        toast.error(res?.data?.message || "Something went wrong");
      }
      if (res?.data?.requiresRegistration) {
        onNext?.();
      } else {
        toast.success(res?.data?.message || "Success");
        if (authentication) {
          if (res?.data?.token) {
            setAccessToken(res.data.token);
            saveToken(res.data.token);
          }
          if (res?.data?.user) {
            setUser({
              ...res.data.user,
              name: res.data.user.fullName || res.data.user.name,
            });
          }
          setAuthLoading(false);
        }
        const prejoinState = {
            meetingId: meetingDetails.meetingId.trim(),
            caseNumber: meetingDetails.caseNumber.trim().toUpperCase(),
            user: res?.data?.user,
            navigateFrom: "public-meeting",
          };
        const opened =
          res?.data?.user?.role === "Advocate" &&
          openAdvocatePrejoinTab(prejoinState);
        if (opened) {
          navigate("/", { replace: true });
          return;
        }

        navigate("/meeting", {
          replace: true,
          state: prejoinState,
        });
      }
    } catch (error: any) {
      toast.error(error?.message || error?.response?.data?.message);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <p className="text-blue-100">{`We've sent a 6-digit code to ${meetingDetails.mobile}. Enter it below to verify your account.`}</p>
      <label className="block text-sm font-medium text-blue-100 mb-4 text-center">
        Enter 6-Digit Code
      </label>
      <OtpInput
        value={otp}
        error={otpError}
        onChange={(value) => {
          setOtp(value);
          if (otpError) setOtpError("");
        }}
      />

      <div className="flex items-center justify-center">
        {canResend ? (
          <button
            type="button"
            onClick={handleResendOTP}
            disabled={loading}
            className="text-sm font-semibold text-white underline-offset-2 transition hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Sending..." : "Resend OTP"}
          </button>
        ) : (
          <p className="text-sm text-blue-100">
            Resend OTP in{" "}
            <span className="font-bold text-amber-300 tabular-nums">
              {counter}s
            </span>
          </p>
        )}
      </div>
      <Button type="submit" fullWidth>
        Verify OTP
      </Button>

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="block w-full text-sm text-blue-200 transition hover:text-white"
        >
          Change meeting details
        </button>
      )}
    </form>
  );
};

export default JoinMeetingOTP;
